import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import useMergeProps from "@store/Framework/Common/Hooks/useMergeProps";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions } from "@store/Framework/Common/Utilities/dataOptions";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import AlertCircleFill from "@store/Library/Components/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill from "@store/Library/Components/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill from "@store/Library/Components/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import InfoFill from "@store/Library/Components/Icons/Info/InfoFill";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

export type InfosBoxTypeOption = DataOption;
export interface InfosBoxTypeOptionMapper {
    info: InfosBoxTypeOption;
    error: InfosBoxTypeOption;
    success: InfosBoxTypeOption;
    warning: InfosBoxTypeOption;
}
export type InfosBoxTypeOptions = DataOptions<InfosBoxTypeOption> & InfosBoxTypeOptionMapper;
export type InfosBoxType = keyof InfosBoxTypeOptionMapper;

export const infosBoxTypeOptions: InfosBoxTypeOptions = {
    info: { id: "info", displayName: "Info", isDefault: true },
    error: { id: "error", displayName: "Error" },
    success: { id: "success", displayName: "Success" },
    warning: { id: "warning", displayName: "Warning" },
};

export type InfosBoxSizeOption = DataOption;
export interface InfosBoxSizeOptionMapper {
    medium: InfosBoxSizeOption;
    small: InfosBoxSizeOption;
}
export type InfosBoxSizeOptions = DataOptions<InfosBoxSizeOption> & InfosBoxSizeOptionMapper;
export type InfosBoxSize = keyof InfosBoxSizeOptionMapper;

export const infosBoxSizeOptions: InfosBoxSizeOptions = {
    medium: { id: "medium", displayName: "Medium", isDefault: true },
    small: { id: "small", displayName: "Small" },
};

export interface InfosBoxProps extends ExpandStyles<InfosBoxStyles>, HTMLAttributes<HTMLElement> {
    type?: InfosBoxType;
    size?: InfosBoxSize;
    showIcon?: boolean;
}

export interface InfosBoxBorderStyles {
    color?: string;
    radius?: string;
    style?: string;
    width?: string;
}

export interface InfosBoxStyles {
    padding?: string;
    color?: string;
    backgroundColor?: string;
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    content?: InjectableCss;
    border?: InfosBoxBorderStyles;
}

export const infosBoxStyles: InfosBoxStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            width: 100%;
            display: flex;
            align-items: center;

            ${({ theme }) => `
                ${cssProp("font-size", theme.design.typography.size.smaller)}
                ${cssProp("line-height", theme.design.typography.lineHeight.semi)}
            `}

            ul, ol, p {
                ${spacingStyles("0.5em", "0.5em")}
            }

            li {
                ${spacingStyles(null, "0.25em")}
            }

            ${breakpoint("sm")} {
                ${x => cssProp("font-size", x.theme.design.typography.size.small)}
            }
        `,
    },
    content: {
        css: css`
            width: 100%;
        `,
    },
    icon: {
        color: "currentColor",
    },
};

export const infosBoxTypes: Partial<Record<InfosBoxType, InfosBoxStyles>> = {
    info: {
        color: "info",
        backgroundColor: "info.light",
        icon: {
            src: InfoFill,
        },
    },
    warning: {
        color: "warning",
        backgroundColor: "warning.light",
        icon: {
            src: AlertTriangleFill,
        },
    },
    error: {
        color: "danger",
        backgroundColor: "danger.light",
        icon: {
            src: AlertCircleFill,
        },
    },
    success: {
        color: "success",
        backgroundColor: "success.light",
        icon: {
            src: CheckmarkCircle2Fill,
        },
    },
};

export const infosBoxSizes: Partial<Record<InfosBoxSize, InfosBoxStyles>> = {
    small: {
        padding: "0.5em",
        icon: {
            size: 24,
        },
    },
    medium: {
        padding: "1em",
        icon: {
            size: 28,
        },
    },
};

const InfosBox: FC<InfosBoxProps> = ({
    type = "info",
    size = "medium",
    showIcon = true,
    children,
    extendedStyles,
    overridedStyles,
    ...htmlAttributes
}) => {
    const definitions = useMergeProps(infosBoxSizes[size], infosBoxTypes[type]);
    const stylesAndDefinitions = useMergeProps(infosBoxStyles, definitions);
    const baseStyles = useExpandProps(stylesAndDefinitions, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        return mergeProps<InfosBoxStyles>(baseStyles, {
            wrapper: {
                css: css`
                    ${({ theme }) => css`
                        ${cssProp("padding", baseStyles.padding)}
                        ${cssProp("color", baseStyles.color, x => resolveColor(x, theme))}
                        ${cssProp("background-color", baseStyles.backgroundColor, x => resolveColor(x, theme))}
                        ${cssProp("border-width", baseStyles.border?.width)}
                        ${cssProp("border-style", baseStyles.border?.style)}
                        ${cssProp("border-color", baseStyles.border?.color, x => resolveColor(x, theme))}
                        ${cssProp("border-radius", baseStyles.border?.radius)}
                    `}
                `,
            },
            icon: {
                css: css`
                    ${cssProp("margin-right", baseStyles.padding)}
                `,
            },
        });
    }, [baseStyles]);

    if (!children) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...htmlAttributes}>
            {showIcon && <Icon {...styles.icon} />}
            <StyledWrapper {...styles.content}>{children}</StyledWrapper>
        </StyledWrapper>
    );
};

export default InfosBox;
